import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpEvent, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { IGenericResponse } from 'src/app/common/interface/generic-response.interface';

@Injectable({
  providedIn: 'root',
})
export class BaseHttpService {
  private baseUrl = environment.BASE_URL;
  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      ...(environment.isLocal && {
        Authorization: 'Bearer ' + localStorage.getItem('auth-token'),
      }),
    }),
  };

  constructor(private httpClient: HttpClient) {}

  public get(path: string, skipLoader?, notHandleError?: boolean): Observable<IGenericResponse> {
    return this.httpClient
      .get<any>(
        `${this.baseUrl}${path}`,
        skipLoader
          ? { headers: new HttpHeaders({ skip: 'true' }) }
          : { headers: new HttpHeaders({}) }
      )
      .pipe(retry(2), catchError(notHandleError ? this.notHandleError : this.handleError));
  }

  getProjects = (
    path: string,
    apiBaseUrl,
    skipLoader?,
    notHandleError?: boolean
  ): Observable<IGenericResponse> => {
    return this.httpClient
      .get<any>(
        `${apiBaseUrl}${path}`,
        skipLoader
          ? { headers: new HttpHeaders({ skip: 'true' }) }
          : { headers: new HttpHeaders({}) }
      )
      .pipe(
        retry(2),
        catchError(notHandleError ? this.notHandleError : this.handleError)
      );
  };

  public post(
    path: string,
    item: any,
    skipLoader?: boolean,
    noRetry?: boolean,
    notHandleError?: boolean
  ): Observable<IGenericResponse> {
    let httpOptionsTemp = { ...this.httpOptions };
    if (skipLoader) {
      httpOptionsTemp.headers = httpOptionsTemp.headers.append('skip', 'true');
    }
    return this.httpClient
      .post<any>(
        `${this.baseUrl}${path}`,
        JSON.stringify(item),
        httpOptionsTemp
      )
      .pipe(retry(noRetry ? 0 : 2), catchError(notHandleError ? this.notHandleError : this.handleError));
  }

  public patch(
    path: string,
    item: any,
    skipLoader?: boolean,
    notHandleError?: boolean,
    retryCount:number = 2
  ): Observable<IGenericResponse> {
    let httpOptionsTemp = { ...this.httpOptions };
    if (skipLoader) {
      httpOptionsTemp.headers = httpOptionsTemp.headers.append('skip', 'true');
    }
    return this.httpClient
      .patch<any>(
        `${this.baseUrl}${path}`,
        JSON.stringify(item),
        httpOptionsTemp
      )
      .pipe(retry(retryCount), catchError(notHandleError ? this.notHandleError : this.handleError));
  }

  public put(path: string, data: any, notHandleError?: boolean, skipLoader?: boolean): Observable<IGenericResponse> {
    let httpOptionsTemp = { ...this.httpOptions };
    if (skipLoader) {
      httpOptionsTemp.headers = httpOptionsTemp.headers.append('skip', 'true');
    }
    return this.httpClient
      .put<any>(`${this.baseUrl}${path}`, data, this.httpOptions)
      .pipe(retry(2), catchError(notHandleError ? this.notHandleError : this.handleError));
  }

  public delete(path: string, data?: any, notHandleError?: boolean): Observable<IGenericResponse> {
    if (data) {
      this.httpOptions['body'] = data;
    }
    return this.httpClient
      .delete<any>(`${this.baseUrl}${path}`, this.httpOptions)
      .pipe(retry(2), catchError(notHandleError ? this.notHandleError : this.handleError));
  }

  public postWithFileUpload(
    path: string,
    item: any,
    options?: any
  ): Observable<Blob | HttpEvent<any> | any> {
    return this.httpClient
      .post<any>(`${this.baseUrl}${path}`, item, options)
      .pipe(
        retry(2),
        catchError((error: HttpErrorResponse) => {
          return throwError(() => error?.error);
        })
      );
  }

  public getBlobFile(endpoint: string, disableLoader: string = 'false')
  {
    return this.httpClient.get(`${this.baseUrl}${endpoint}`, {
      responseType: 'blob' as 'json',
      observe: 'response',
      headers: new HttpHeaders({ skip: disableLoader })
    }).pipe(
      retry(2),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error?.error);
      })
    );;
  }

  public postBlobFile(endpoint: string, payload: any) {
    return this.httpClient.post(`${this.baseUrl}${endpoint}`, payload, {
      responseType: 'blob' as 'json',
      observe: 'response',
      headers: new HttpHeaders({ skip: 'true' })
    }).pipe(
      retry(2),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error?.error);
      })
    );;
  }

  private handleError(error: HttpErrorResponse) {
    let errorMessage;
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = error?.error?.error ? error?.error?.error : error?.message;
    }

    return throwError(() => errorMessage);
  }

  private notHandleError(error: HttpErrorResponse) {
    let errorValue;
    if (error.error instanceof ErrorEvent) {
      errorValue = error?.error;
    } else {
      errorValue = error?.error ? error?.error : error;
    }

    return throwError(() => errorValue);
  }

  /**
   * GetScanningHeaders: is to get the headers required for file uploading with sasUrl
   *
   * @param fileType string
   * @returns HttpHeaders
   */
  getScanningHeaders = (fileType: string):HttpHeaders => {
    const httpOptionsScanningHeaders = new HttpHeaders({
      "x-ms-blob-type": "BlockBlob",
      "Content-Type": fileType
    })

    return httpOptionsScanningHeaders;
  };

  /**
   * Sends a POST request to the specified endpoint with the given payload and expects a Blob response.
   *
   * @param endpoint string
   * @param payload Object.
   * @returns An Observable that emits the HTTP response containing the Blob data.
   */
  getBlobFilePost = (endpoint: string, payload:any) => {
    return this.httpClient.post(`${this.baseUrl}${endpoint}`, payload,{ responseType: 'blob' as 'json',
      observe: 'response',}).pipe(
      retry(2),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error?.error);
      })
    );
  }

  /**
   * Sends a PUT request to the specified endpoint with the given payload and expects a Blob response.
   *
   * @param endpoint string
   * @param payload Object.
   * @returns An Observable that emits the HTTP response containing the Blob data.
   */
  getBlobFilePut = (endpoint: string, payload:any) => {
    return this.httpClient.put(`${this.baseUrl}${endpoint}`, payload,{ responseType: 'blob' as 'json',
      observe: 'response',}).pipe(
      retry(2),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => error?.error);
      })
    );
  }

  /**
   * Get the status of GenAI generated report endpoint. 
   * 
   * @param endpoint - string
   */
  getGenAIStatus=(endpoint:string):Observable<IGenericResponse>=>{
    return this.httpClient.get<IGenericResponse>(`${this.baseUrl}${endpoint}`,
      { headers: new HttpHeaders({ skip: 'true' })}
    ).pipe(
      retry(2),
      catchError(this.handleError)
    );
  }

}
